import api from './client'

export interface InputFieldOption {
  id: string
  name: string
  description?: string
}

export interface InputField {
  key: string
  type: 'textarea' | 'text' | 'select' | 'button_group' | 'image_upload' | 'number'
  label: string
  required?: boolean
  placeholder?: string
  max_length?: number
  max_count?: number
  options?: InputFieldOption[]
}

export interface PipelineStepConfig {
  step: number
  type: 'text' | 'image' | 'video' | 'analyze'
  title: string
  description: string
  credits_estimate: number
  // Акция тренда: промо-цена шага до даты (ISO). Бэк списывает именно её —
  // виджет обязан показывать на кнопке этапа ту же цифру.
  discount_price?: number
  discount_until?: string
  // Формат этапа (9:16 / 1:1 / …) — рамка результата в виджете под него.
  aspect_ratio?: string
}

/** Действующая цена шага: промо при живой акции, иначе базовая цена шаблона. */
export function stepEffectiveCost(s?: PipelineStepConfig | null): number {
  if (!s) return 0
  if (s.discount_price && s.discount_until && new Date(s.discount_until).getTime() > Date.now())
    return s.discount_price
  return s.credits_estimate ?? 0
}

export interface PipelineTypeConfig {
  title: string
  description: string
  total_steps: number
  steps: PipelineStepConfig[]
  input_schema?: { fields: InputField[] }
  icon?: string
}

export interface PipelineStepAttempt {
  request_id?: string
  result_url: string
  created_at?: string
}

export interface PipelineStep {
  step: number
  type: string
  title: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  request_id?: string
  credits_spent?: number
  // Динамическая схема шага (зависит от типа: prompt/image_url/… и
  // text/result_url/…) — конкретные поля narrow'ятся на read-сайтах.
  input: Record<string, unknown> & { prompt?: string; image_url?: string }
  output: Record<string, unknown> & {
    text?: string
    result_url?: string
    result_urls?: string[]
    product_name?: string
    category?: string
    qualities?: string[]
    error?: string
  }
  /** Версии результата шага («сгенерировать ещё» копит, не затирает);
   *  выбранная = output.result_url. */
  attempts?: PipelineStepAttempt[]
}

export interface PipelineJob {
  id: string
  pipeline_type: string
  status: string
  current_step: number
  total_steps: number
  config: Record<string, unknown>
  steps: PipelineStep[]
  total_credits: number
  error_message?: string
  created_at: string
  updated_at: string
}

export async function getPipelineTypes(): Promise<Record<string, PipelineTypeConfig>> {
  const res = await api.request<{ ok: boolean; types: Record<string, PipelineTypeConfig> }>(
    '/api/v1/pipelines/types', { method: 'GET' }
  )
  return res.types || {}
}

export async function startPipeline(
  pipelineType: string, config: Record<string, unknown>
): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request('/api/v1/pipelines/start', {
    method: 'POST', body: { pipeline_type: pipelineType, config }
  })
}

export async function getPipelineJob(jobId: string): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request(`/api/v1/pipelines/${jobId}`, { method: 'GET' })
}

export async function proceedPipeline(
  jobId: string,
  configUpdate?: Record<string, unknown>
): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request(`/api/v1/pipelines/${jobId}/proceed`, {
    method: 'POST',
    body: configUpdate ? { config_update: configUpdate } : {},
  })
}

export async function retryPipeline(jobId: string): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request(`/api/v1/pipelines/${jobId}/retry`, { method: 'POST' })
}

/** Выбор версии результата шага (из attempts) — из какого фото делать видео. */
export async function selectStepResult(
  jobId: string, stepIdx: number, resultUrl: string
): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request(`/api/v1/pipelines/${jobId}/steps/${stepIdx}/select`, {
    method: 'POST', body: { result_url: resultUrl },
  })
}

/** «Сгенерировать ещё» — повторный платный запуск media-шага, версии копятся. */
export async function regenerateStep(
  jobId: string, stepIdx: number
): Promise<{ ok: boolean; job: PipelineJob }> {
  return api.request(`/api/v1/pipelines/${jobId}/steps/${stepIdx}/regenerate`, {
    method: 'POST',
  })
}

export async function cancelPipeline(jobId: string): Promise<{ ok: boolean }> {
  return api.request(`/api/v1/pipelines/${jobId}`, { method: 'DELETE' })
}

export async function getMyPipelines(
  limit = 20, offset = 0
): Promise<{ ok: boolean; jobs: PipelineJob[]; total: number }> {
  return api.request(`/api/v1/pipelines/my?limit=${limit}&offset=${offset}`, { method: 'GET' })
}
